"use client";

import Link from "next/link";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { AuthNote, StatusPanel } from "@/components/auth-ui";
import { CodeInput } from "@/components/auth-controls";
import { Button } from "@/components/ui/button";
import { toUserErrorMessage } from "@/lib/error-config";
import { useFacilityService } from "@/lib/facility-service";
import { useFrappeSession } from "@/lib/frappe-session";
import { clearMfaContext, readMfaContext } from "@/lib/mfa-flow";
import { resolvePostLoginPath } from "@/lib/navigation";

/* Frappe 2FA step 2: the one-time code after the password. Used by the
   sign-in code page (/sign-in/verify) and inline on the stepper's
   "Email verified" card, so onboarding never leaves the stepper. */

const CODE_LENGTH = 6;

export function SignInCodeForm({ hasContext = true, onRestart }: {
  /** False when no password step is pending (the code page opened directly). */
  hasContext?: boolean;
  /** Back to the password form in place; without it the links go to /sign-in. */
  onRestart?: () => void;
}) {
  const [code, setCode] = useState(() => Array.from({ length: CODE_LENGTH }, () => ""));
  const [seconds, setSeconds] = useState(300);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useFrappeSession();
  const facilityService = useFacilityService();

  useEffect(() => {
    const timer = window.setInterval(() => setSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const updateCode = (next: string[]) => { setCode(next); if (next.every(Boolean)) setError(""); };
  const verify = async (event: React.FormEvent) => {
    event.preventDefault(); setError(""); setIsSubmitting(true);
    const pending = readMfaContext();
    if (!pending?.tmpId) {
      setError("Your sign-in attempt has expired. Start again from the sign-in page."); setIsSubmitting(false); return;
    }
    try {
      // Frappe 2FA step 2: echo the tmp_id from step 1 along with the OTP.
      await login({ otp: code.join(""), tmp_id: pending.tmpId });
      const nextPath = pending.nextPath ?? "/dashboard";
      clearMfaContext();
      // Onboarding gate: facilities still on the stepper resume setup
      // instead of landing on the dashboard.
      window.location.assign(await resolvePostLoginPath(() => facilityService.onboardingStatus(), nextPath));
    } catch (verificationError) {
      // Success stays busy until the page unloads, so the code can't be resubmitted.
      setError(toUserErrorMessage(verificationError, "VERIFICATION_FAILED")); setIsSubmitting(false);
    }
  };
  const formatted = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  const restart = (label: string, className?: string) => onRestart
    ? <button type="button" className={`${className ?? "auth-text-link"} link-button`} onClick={onRestart}>{label}</button>
    : <Link className={className ?? "auth-text-link"} href="/sign-in">{label}</Link>;

  return <form className="auth-stack" onSubmit={verify}>
    <CodeInput label="Six digit verification code" value={code} onChange={updateCode} />
    <div className="auth-meta-row">
      <span>Didn’t get it? {restart("Start again")}</span>
      <span>{seconds > 0 ? <>Code expires in <strong>{formatted}</strong></> : <strong>Code expired</strong>}</span>
    </div>
    {error && <StatusPanel tone="danger" title="Verification failed">{error}</StatusPanel>}
    <Button type="submit" disabled={isSubmitting || code.join("").length !== CODE_LENGTH || !hasContext}>{isSubmitting ? <><LoaderCircle size={16} className="spin" />Verifying…</> : "Verify and sign in"}</Button>
    <AuthNote icon={ShieldCheck}>Never share this code. Facility360 staff will never ask for it.</AuthNote>
    {restart("Use a different account")}
  </form>;
}
